import React, { useEffect, useMemo, useState } from 'react';
import {
  addDoc,
  collection,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp
} from 'firebase/firestore';
import { ArrowLeft, CheckSquare, ClipboardList, Loader2, Plus, Square } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getDownloadURL, ref, storage, uploadBytes } from './firebaseUtils.js';
import MultiFileUpload from './components/MultiFileUpload.js';
import ListFilterBar from './components/ListFilterBar.js';
import { useConfig } from './hooks/useConfig.js';
import { formatVehicleNumber, normalizeVehicleNumber } from './utils/vehicleNumber.js';

const organisationOptions = ['NIPL(MH)', 'NIPL(RJ)', 'CLOUD(MH)', 'CLOUD(RJ)', 'JE'];
const warehouseOptions = ['Mumbai', 'Hyderabad', 'Ahmedabad', 'Jaipur', 'Delhi'];
const pickupTypeOptions = ['Rental Return', 'Sales Return', 'Purchase Inwards', 'Interbrach'];
const pickupAgentOptions = [
  'PRASHANT',
  'ANKIT',
  'DEEPAK Verma',
  'PRANAV MAACHI',
  'DEEPAK KUMAR',
  'Susheel',
  'Satyendra',
  'Shahid',
  'Anand',
  'Suryakant',
  'Sachin',
  'Self',
  'Ram Chaudhary',
  'Jai Jangid'
];

const verifiedByOptions = [
  'Rajesh Kumar',
  'Susheel Sahu',
  'Shahid Ali',
  'Satyendra Sahu',
  'Pranav Machhi',
  'Deepak Verma',
  'Prashant Dhar',
  'Shakti Singh',
  'Ankit Jangid',
  'Govind Suthar',
  'Deepak Kapoor',
  'Piyush Agarwal',
  'Ram Choudhary',
  'Jai Jangid'
];

const initialFormState = {
  dateOfPickup: '',
  serialNoMic: '',
  serialNoPickupChallan: '',
  organisation: '',
  warehouse: '',
  pickupType: '',
  vehicleType: '',
  transport: '',
  customerSupplierName: '',
  projectName: '',
  emailClientOffice: '',
  emailProjectOffice: '',
  weightKgs: '',
  missingDamages: 'No',
  vehicleNumber: '',
  pickupAgent: '',
  verifiedBy: '',
  missingDamageImages: [],
  vehicleImages: [],
  micFiles: [],
  pcFiles: [],
  checklists: {
    informedHo: false,
    paperShared: false,
    damagePartsSent: false,
    invoiceSent: false
  }
};

const collectionName = 'InwardsLog';

const InwardsLog = ({ db, user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { config } = useConfig();
  const isCreatePage = location.pathname.endsWith('/new');
  const [entries, setEntries] = useState([]);
  const [loadingEntries, setLoadingEntries] = useState(true);
  const [formData, setFormData] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [filterValues, setFilterValues] = useState({
    search: '',
    warehouse: 'All',
    pickupType: 'All'
  });

  const tableColumns = useMemo(() => ([
    'Date',
    'Customer',
    'Pickup Challan No.',
    'Organisation',
    'Warehouse',
    'Pickup Type',
    'Vehicle No.'
  ]), []);

  const transportOptions = useMemo(() => {
    const source = Array.isArray(config?.transporters) ? config.transporters : [];
    const normalized = source
      .map((item) => String(item || '').replace(/^#\s*/, '').trim())
      .filter(Boolean);

    return Array.from(new Set(normalized));
  }, [config?.transporters]);

  const vehicleTypeOptions = useMemo(() => {
    const source = Array.isArray(config?.vehicle_types) ? config.vehicle_types : [];
    const normalized = source
      .map((item) => String(item || '').trim())
      .filter(Boolean);

    return Array.from(new Set(normalized));
  }, [config?.vehicle_types]);

  const filteredEntries = useMemo(() => {
    const search = filterValues.search.trim().toLowerCase();
    const normalizedSearch = normalizeVehicleNumber(search);

    return entries.filter((entry) => {
      const matchesSearch = !search || [
        entry.serialNoMic,
        entry.serialNoPickupChallan,
        entry.organisation,
        entry.warehouse,
        entry.pickupType,
        entry.vehicleNumber,
        entry.pickupAgent,
        entry.verifiedBy,
        entry.customerSupplierName,
        entry.projectName
      ].some((value) => String(value || '').toLowerCase().includes(search));
      const matchesVehicleNumber = !normalizedSearch
        || normalizeVehicleNumber(entry.vehicleNumber).includes(normalizedSearch);

      const matchesWarehouse = filterValues.warehouse === 'All' || entry.warehouse === filterValues.warehouse;
      const matchesPickupType = filterValues.pickupType === 'All' || entry.pickupType === filterValues.pickupType;

      return (matchesSearch || matchesVehicleNumber) && matchesWarehouse && matchesPickupType;
    });
  }, [entries, filterValues]);

  useEffect(() => {
    if (isCreatePage) return undefined;

    setLoadingEntries(true);
    const q = query(collection(db, collectionName), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const nextEntries = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
      setEntries(nextEntries);
      setLoadingEntries(false);
    }, (error) => {
      console.error('Error loading Inwards Log entries:', error);
      setLoadingEntries(false);
    });

    return () => unsubscribe();
  }, [db, isCreatePage]);

  const handleChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === 'missingDamages' && value === 'No'
        ? { missingDamageImages: [] }
        : {})
    }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
    if (name === 'missingDamages' && value === 'No') {
      setErrors((prev) => ({ ...prev, missingDamageImages: '' }));
    }
  };

  const handleVehicleNumberChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      vehicleNumber: formatVehicleNumber(value)
    }));
    setErrors((prev) => ({ ...prev, vehicleNumber: '' }));
  };

  const handleFileChange = (name, files) => {
    const nextFiles = Array.from(files);
    setFormData((prev) => ({ ...prev, [name]: nextFiles }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const toggleChecklist = (name) => {
    setFormData((prev) => ({
      ...prev,
      checklists: {
        ...prev.checklists,
        [name]: !prev.checklists[name]
      }
    }));
  };

  const validateForm = () => {
    const nextErrors = {};
    const requiredFields = [
      ['dateOfPickup', 'Date of Pickup'],
      ['serialNoMic', 'Serial No. MIC'],
      ['serialNoPickupChallan', 'Serial No. Pickup challan / Inwards challan'],
      ['organisation', 'Organisation'],
      ['warehouse', 'Warehouse'],
      ['pickupType', 'Pickup Type'],
      ['vehicleType', 'Vehicle Type'],
      ['transport', 'Transport'],
      ['customerSupplierName', 'Customer / Supplier Name'],
      ['projectName', 'Project Name'],
      ['emailClientOffice', 'Email - Client office'],
      ['emailProjectOffice', 'Email - Project office'],
      ['weightKgs', 'Weight (kgs)'],
      ['vehicleNumber', 'Vehicle Number'],
      ['pickupAgent', 'Pickup Agent'],
      ['verifiedBy', 'Verified By']
    ];

    requiredFields.forEach(([key, label]) => {
      if (!String(formData[key] || '').trim()) nextErrors[key] = `${label} is required.`;
    });

    if (!formData.vehicleImages.length) nextErrors.vehicleImages = 'Vehicle image proof is required.';
    if (!formData.micFiles.length) nextErrors.micFiles = 'Upload MIC is required.';
    if (!formData.pcFiles.length) nextErrors.pcFiles = 'Upload PC / Inwards Challan is required.';
    if (formData.missingDamages === 'Yes' && !formData.missingDamageImages.length) {
      nextErrors.missingDamageImages = 'Upload Image is required when Missing / Damages is Yes.';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const uploadFile = async (file, path) => {
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file);
    const url = await getDownloadURL(storageRef);
    return {
      name: file.name,
      type: file.type,
      size: file.size,
      url
    };
  };

  const uploadFileGroup = async (files, folderName, allowMultiple = true) => {
    if (!files.length) return [];
    const selectedFiles = allowMultiple ? files : files.slice(0, 1);

    return Promise.all(
      selectedFiles.map((file) => {
        const safeName = String(file.name || 'file').replace(/[^\w.\-() ]+/g, '_');
        const path = `inwards_log/${Date.now()}_${folderName}/${safeName}`;
        return uploadFile(file, path);
      })
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const vehicleImages = await uploadFileGroup(formData.vehicleImages, 'vehicle_images', true);
      const micFiles = await uploadFileGroup(formData.micFiles, 'mic_files', true);
      const pcFiles = await uploadFileGroup(formData.pcFiles, 'pc_files', true);
      const missingDamageImages = formData.missingDamages === 'Yes'
        ? await uploadFileGroup(formData.missingDamageImages, 'missing_damage_images', true)
        : [];

      const payload = {
        ...formData,
        vehicleNumber: normalizeVehicleNumber(formData.vehicleNumber),
        vehicleImages,
        micFiles,
        pcFiles,
        missingDamageImages,
        createdBy: user?.displayName || user?.email || '',
        createdByEmail: user?.email || '',
        createdByRole: user?.role || '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      await addDoc(collection(db, collectionName), payload);
      setFormData(initialFormState);
      setErrors({});
      navigate('/ops/inwards-log');
    } catch (error) {
      console.error('Error saving Inwards Log entry:', error);
      alert(`Failed to save Inwards Log entry: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isCreatePage) {
    return (
      <div style={pageStyle} className="inwards-log-page">
        <div style={headerRowStyle} className="inwards-log-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={iconWrapStyle}>
              <ClipboardList size={28} color="#0f766e" />
            </div>
            <div>
              <h1 style={titleStyle}>Create MIC</h1>
              <p style={descriptionStyle}>Fill the inward capture form and submit it to the InwardsLog collection.</p>
            </div>
          </div>
          <button onClick={() => navigate('/ops/inwards-log')} style={primaryButtonStyle} type="button">
            <ArrowLeft size={16} />
            Back to Log
          </button>
        </div>

        <form onSubmit={handleSubmit} style={formStyle} className="inwards-log-form">
          <Section title="Basic Details">
            <Grid className="inwards-log-grid">
              <Field label="Date of Pickup" required error={errors.dateOfPickup}>
                <input type="date" value={formData.dateOfPickup} onChange={(e) => handleChange('dateOfPickup', e.target.value)} style={inputStyle} required />
              </Field>
              <Field label="Serial No. MIC" required error={errors.serialNoMic}>
                <input type="text" value={formData.serialNoMic} onChange={(e) => handleChange('serialNoMic', e.target.value)} style={inputStyle} required />
              </Field>
              <Field label="Serial No. Pickup challan / Inwards challan" required error={errors.serialNoPickupChallan}>
                <input type="text" value={formData.serialNoPickupChallan} onChange={(e) => handleChange('serialNoPickupChallan', e.target.value)} style={inputStyle} required />
              </Field>
              <Field label="Organisation" required error={errors.organisation}>
                <select value={formData.organisation} onChange={(e) => handleChange('organisation', e.target.value)} style={inputStyle} required>
                  <option value="">Select organisation</option>
                  {organisationOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                </select>
              </Field>
              <Field label="Select warehouse" required error={errors.warehouse}>
                <select value={formData.warehouse} onChange={(e) => handleChange('warehouse', e.target.value)} style={inputStyle} required>
                  <option value="">Select warehouse</option>
                  {warehouseOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                </select>
              </Field>
              <Field label="Pickup Type" required error={errors.pickupType}>
                <select value={formData.pickupType} onChange={(e) => handleChange('pickupType', e.target.value)} style={inputStyle} required>
                  <option value="">Select pickup type</option>
                  {pickupTypeOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                </select>
              </Field>
              <Field label="Vehicle type" required error={errors.vehicleType}>
                <select value={formData.vehicleType} onChange={(e) => handleChange('vehicleType', e.target.value)} style={inputStyle} required>
                  <option value="">Select vehicle type</option>
                  {vehicleTypeOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                </select>
              </Field>
              <Field label="Transport" required error={errors.transport}>
                <select value={formData.transport} onChange={(e) => handleChange('transport', e.target.value)} style={inputStyle} required>
                  <option value="">Select transport</option>
                  {transportOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                </select>
              </Field>
            </Grid>
          </Section>

          <Section title="Site And Project Details">
            <Grid className="inwards-log-grid">
              <Field label="Customer / Supplier Name" required error={errors.customerSupplierName}>
                <input type="text" value={formData.customerSupplierName} onChange={(e) => handleChange('customerSupplierName', e.target.value)} style={inputStyle} required />
              </Field>
              <Field label="Project Name" required error={errors.projectName}>
                <input type="text" value={formData.projectName} onChange={(e) => handleChange('projectName', e.target.value)} style={inputStyle} required />
              </Field>
              <Field label="Email- Client office" required error={errors.emailClientOffice}>
                <input type="email" value={formData.emailClientOffice} onChange={(e) => handleChange('emailClientOffice', e.target.value)} style={inputStyle} required />
              </Field>
              <Field label="Email - Project office" required error={errors.emailProjectOffice}>
                <input type="email" value={formData.emailProjectOffice} onChange={(e) => handleChange('emailProjectOffice', e.target.value)} style={inputStyle} required />
              </Field>
              <Field label="Weight (kgs)" required error={errors.weightKgs}>
                <input type="number" min="0" step="0.01" value={formData.weightKgs} onChange={(e) => handleChange('weightKgs', e.target.value)} style={inputStyle} required />
              </Field>
              <Field label="Missing / Damages" required error={errors.missingDamages}>
                <select value={formData.missingDamages} onChange={(e) => handleChange('missingDamages', e.target.value)} style={inputStyle} required>
                  <option value="No">No</option>
                  <option value="Yes">Yes</option>
                </select>
              </Field>
              {formData.missingDamages === 'Yes' && (
                <Field label="Upload Image Field" required error={errors.missingDamageImages}>
                  <MultiFileUpload
                    value={formData.missingDamageImages}
                    onFilesChange={(files) => handleFileChange('missingDamageImages', files)}
                    allowedTypes={['*/*']}
                    maxSizeMB={0}
                    multiple
                    helperText="Upload one or more images/documents for the missing or damaged items."
                    externalError={errors.missingDamageImages}
                  />
                </Field>
              )}
            </Grid>
          </Section>

          <Section title="Upload Document Section">
            <Grid className="inwards-log-grid">
              <Field label="Vehicle image proof" required error={errors.vehicleImages}>
                <MultiFileUpload
                  value={formData.vehicleImages}
                  onFilesChange={(files) => handleFileChange('vehicleImages', files)}
                  allowedTypes={['*/*']}
                  maxSizeMB={0}
                  multiple
                  helperText="Multiple uploads allowed. Any file format is accepted."
                  externalError={errors.vehicleImages}
                />
              </Field>
              <Field label="Upload MIC" required error={errors.micFiles}>
                <MultiFileUpload
                  value={formData.micFiles}
                  onFilesChange={(files) => handleFileChange('micFiles', files)}
                  allowedTypes={['*/*']}
                  maxSizeMB={0}
                  multiple
                  helperText="Multiple uploads allowed. Any file format is accepted."
                  externalError={errors.micFiles}
                />
              </Field>
              <Field label="Upload PC / Inwards Challan" required error={errors.pcFiles}>
                <MultiFileUpload
                  value={formData.pcFiles}
                  onFilesChange={(files) => handleFileChange('pcFiles', files)}
                  allowedTypes={['*/*']}
                  maxSizeMB={0}
                  multiple
                  helperText="Multiple uploads allowed. Any file format is accepted."
                  externalError={errors.pcFiles}
                />
              </Field>
            </Grid>
          </Section>

          <div style={sideBySideSectionRowStyle} className="inwards-log-side-by-side">
            <Section title="Final Check">
              <Checklist
                checked={formData.checklists.informedHo}
                label="INFORMED TO HO FOR MISSING AND DAMAGES (WHATSAPP / SLACK)"
                onChange={() => toggleChecklist('informedHo')}
              />
              <Checklist
                checked={formData.checklists.paperShared}
                label="PAPER SHARED TO OFFICE (WHATSAPP / SLACK)"
                onChange={() => toggleChecklist('paperShared')}
              />
              <Checklist
                checked={formData.checklists.damagePartsSent}
                label="DAMAGE PARTS SENT FOR MAINTENANCE (IF ANY)"
                onChange={() => toggleChecklist('damagePartsSent')}
              />
              <Checklist
                checked={formData.checklists.invoiceSent}
                label="INVOICE / PAPERS SENT TO ACCOUNTS"
                onChange={() => toggleChecklist('invoiceSent')}
              />
            </Section>

            <Section title="Transport Details">
              <Grid className="inwards-log-grid">
                <Field label="Vehicle number" required error={errors.vehicleNumber}>
                  <input
                    type="text"
                    value={formData.vehicleNumber}
                    onChange={(e) => handleVehicleNumberChange(e.target.value)}
                    style={inputStyle}
                    required
                    placeholder="e.g. MH-AB-CD-1234"
                    maxLength={13}
                    autoComplete="off"
                    autoCapitalize="characters"
                    spellCheck={false}
                    inputMode="text"
                  />
                </Field>
                <Field label="Pickup agent" required error={errors.pickupAgent}>
                  <select value={formData.pickupAgent} onChange={(e) => handleChange('pickupAgent', e.target.value)} style={inputStyle} required>
                    <option value="">Select pickup agent</option>
                    {pickupAgentOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                  </select>
                </Field>
                <Field label="Verified by" required error={errors.verifiedBy}>
                  <select value={formData.verifiedBy} onChange={(e) => handleChange('verifiedBy', e.target.value)} style={inputStyle} required>
                    <option value="">Select verifier</option>
                    {verifiedByOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                  </select>
                </Field>
              </Grid>
            </Section>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button type="submit" style={submitButtonStyle} disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </form>

        {isSubmitting && (
          <div style={submittingOverlayStyle} role="status" aria-live="polite">
            <div style={submittingCardStyle}>
              <Loader2 size={22} color="#0f766e" className="animate-spin" />
              <div>
                <div style={submittingTitleStyle}>Submitting...</div>
                <div style={submittingTextStyle}>Please wait while the record is being saved.</div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={pageStyle} className="inwards-log-page">
      <div style={headerRowStyle} className="inwards-log-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <div style={iconWrapStyle}>
            <ClipboardList size={28} color="#0f766e" />
          </div>
          <div>
              <h1 style={titleStyle}>Inwards Log</h1>
            <p style={descriptionStyle}>Live inward log entries pulled from the InwardsLog collection.</p>
          </div>
        </div>
        <button onClick={() => navigate('/ops/inwards-log/new')} style={primaryButtonStyle} type="button">
          <Plus size={16} />
          Inwards Log
        </button>
      </div>

      <div style={tableCardStyle} className="inwards-log-table-card">
        <div style={{ padding: '16px 16px 0' }}>
          <ListFilterBar
            fields={[
              {
                key: 'search',
                type: 'search',
                label: 'Search',
                placeholder: 'Search MIC, challan, vehicle, pickup agent...'
              },
              {
                key: 'warehouse',
                type: 'select',
                label: 'Warehouse',
                options: ['All', ...warehouseOptions]
              },
              {
                key: 'pickupType',
                type: 'select',
                label: 'Pickup Type',
                options: ['All', ...pickupTypeOptions]
              }
            ]}
            values={filterValues}
            onFieldChange={(key, value) => setFilterValues((prev) => ({ ...prev, [key]: value }))}
          />
        </div>
        <div style={tableWrapStyle}>
          <table style={tableStyle}>
            <thead>
              <tr>
                {tableColumns.map((column) => (
                  <th key={column} style={thStyle}>{column}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loadingEntries ? (
                <tr>
                  <td colSpan={tableColumns.length} style={{ ...tdStyle, padding: '28px', color: '#64748b' }}>
                    Loading entries...
                  </td>
                </tr>
              ) : filteredEntries.length > 0 ? (
                filteredEntries.map((entry) => (
                  <tr key={entry.id} onClick={() => navigate(`/ops/inwards-log/${entry.id}`)} style={{ cursor: 'pointer' }}>
                    <td style={tdStyle}>{formatDate(entry.dateOfPickup || entry.createdAt)}</td>
                    <td style={tdStyle}>{entry.customerSupplierName || '---'}</td>
                    <td style={tdStyle}>{entry.serialNoPickupChallan || '---'}</td>
                    <td style={tdStyle}>{entry.organisation || '---'}</td>
                    <td style={tdStyle}>{entry.warehouse || '---'}</td>
                    <td style={tdStyle}>{entry.pickupType || '---'}</td>
                    <td style={tdStyle}>{formatVehicleNumber(entry.vehicleNumber) || '---'}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={tableColumns.length} style={{ ...tdStyle, padding: '28px', color: '#64748b' }}>
                    {entries.length > 0
                      ? 'No matching inwards log entries found.'
                      : (
                        <>
                          No inwards log entries yet. Use <strong>Inwards Log</strong> to add the first record.
                        </>
                      )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
};

const formatDate = (value) => {
  if (!value) return '---';
  if (typeof value?.toDate === 'function') {
    return value.toDate().toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '---';
  return date.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
};

const Section = ({ title, children }) => (
  <section style={sectionCardStyle}>
    <h2 style={sectionTitleStyle}>{title}</h2>
    {children}
  </section>
);

const Field = ({ label, required, error, children }) => (
  <div style={fieldStyle}>
    <span style={labelStyle}>
      {label}
      {required && <span style={requiredMarkStyle}> *</span>}
    </span>
    {children}
    {error && <span style={errorStyle}>{error}</span>}
  </div>
);

const Checklist = ({ checked, label, onChange }) => (
  <button type="button" onClick={onChange} style={checklistItemStyle}>
    {checked ? <CheckSquare size={18} color="#0f766e" /> : <Square size={18} color="#94a3b8" />}
    <span>{label}</span>
  </button>
);

const pageStyle = {
  maxWidth: '1220px',
  margin: '0 auto',
  padding: '20px 0'
};

const headerRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: '16px',
  marginBottom: '18px'
};

const titleStyle = {
  margin: 0,
  fontSize: '30px',
  lineHeight: 1.15,
  color: '#0f172a'
};

const descriptionStyle = {
  margin: '10px 0 0 0',
  maxWidth: '760px',
  color: '#475569',
  lineHeight: 1.65
};

const iconWrapStyle = {
  width: '56px',
  height: '56px',
  borderRadius: '16px',
  backgroundColor: '#d1fae5',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0
};

const primaryButtonStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  border: 'none',
  borderRadius: '10px',
  padding: '11px 16px',
  backgroundColor: '#0f766e',
  color: 'white',
  fontSize: '14px',
  fontWeight: 700,
  cursor: 'pointer',
  whiteSpace: 'nowrap'
};

const formStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px'
};

const sectionCardStyle = {
  backgroundColor: 'white',
  borderRadius: '16px',
  border: '1px solid #e2e8f0',
  padding: '18px',
  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.05)'
};

const sectionTitleStyle = {
  margin: '0 0 14px 0',
  fontSize: '16px',
  color: '#0f172a'
};

const grid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: '14px'
};

const Grid = ({ children, className = '' }) => <div style={grid} className={className}>{children}</div>;

const fieldStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '6px'
};

const labelStyle = {
  fontSize: '12px',
  fontWeight: 700,
  color: '#334155'
};

const requiredMarkStyle = {
  color: '#dc2626'
};

const errorStyle = {
  fontSize: '12px',
  color: '#dc2626',
  fontWeight: 600
};

const inputStyle = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '11px 12px',
  borderRadius: '10px',
  border: '1px solid #cbd5e1',
  fontSize: '14px',
  color: '#0f172a',
  outline: 'none',
  backgroundColor: 'white'
};

const checklistItemStyle = {
  width: '100%',
  display: 'flex',
  alignItems: 'flex-start',
  gap: '10px',
  padding: '12px 14px',
  borderRadius: '10px',
  border: '1px solid #e2e8f0',
  backgroundColor: '#f8fafc',
  color: '#0f172a',
  fontSize: '13px',
  textAlign: 'left',
  cursor: 'pointer',
  marginBottom: '10px'
};

const sideBySideSectionRowStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
  gap: '16px'
};

const submitButtonStyle = {
  border: 'none',
  borderRadius: '10px',
  padding: '12px 18px',
  backgroundColor: '#16a34a',
  color: 'white',
  fontSize: '14px',
  fontWeight: 700,
  cursor: 'pointer'
};

const submittingOverlayStyle = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(15, 23, 42, 0.45)',
  backdropFilter: 'blur(4px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 10000,
  padding: '20px'
};

const submittingCardStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '14px',
  backgroundColor: 'white',
  borderRadius: '16px',
  padding: '18px 20px',
  boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.12)',
  border: '1px solid #e2e8f0',
  minWidth: '280px'
};

const submittingTitleStyle = {
  fontSize: '16px',
  fontWeight: 800,
  color: '#0f172a'
};

const submittingTextStyle = {
  marginTop: '4px',
  fontSize: '13px',
  color: '#64748b'
};

const tableCardStyle = {
  backgroundColor: 'white',
  borderRadius: '16px',
  border: '1px solid #e2e8f0',
  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.05)',
  overflow: 'hidden'
};

const tableWrapStyle = {
  overflowX: 'auto'
};

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  minWidth: '1100px'
};

const thStyle = {
  padding: '14px 12px',
  backgroundColor: '#f8fafc',
  color: '#475569',
  fontSize: '11px',
  fontWeight: 800,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  borderBottom: '1px solid #e2e8f0',
  whiteSpace: 'nowrap',
  textAlign: 'left',
  verticalAlign: 'middle'
};

const tdStyle = {
  padding: '14px 12px',
  borderBottom: '1px solid #eef2f7',
  color: '#0f172a',
  fontSize: '13px',
  whiteSpace: 'nowrap',
  textAlign: 'left',
  verticalAlign: 'middle'
};

if (typeof document !== 'undefined' && !document.getElementById('inwards-log-responsive-style')) {
  const styleSheet = document.createElement('style');
  styleSheet.id = 'inwards-log-responsive-style';
  styleSheet.innerHTML = `
    @media (max-width: 900px) {
      .inwards-log-header {
        flex-direction: column !important;
        align-items: stretch !important;
      }

      .inwards-log-header button {
        width: 100%;
        justify-content: center;
      }

      .inwards-log-grid {
        grid-template-columns: 1fr !important;
      }

      .inwards-log-side-by-side {
        grid-template-columns: 1fr !important;
      }

      .inwards-log-table-card {
        border-radius: 12px;
      }

      .inwards-log-page {
        padding: 16px 0 !important;
      }
    }

    @media (max-width: 640px) {
      .inwards-log-page {
        padding: 12px 0 !important;
      }

      .inwards-log-header {
        gap: 12px !important;
        margin-bottom: 14px !important;
      }

      .inwards-log-header h1 {
        font-size: 24px !important;
      }

      .inwards-log-header p {
        margin-top: 6px !important;
      }

      .inwards-log-form {
        gap: 12px !important;
      }

      .inwards-log-grid {
        gap: 12px !important;
      }

      .inwards-log-side-by-side {
        gap: 12px !important;
      }

      .inwards-log-table-card th,
      .inwards-log-table-card td {
        padding: 10px 8px !important;
        font-size: 12px !important;
      }
    }
  `;
  document.head.appendChild(styleSheet);
}

export default InwardsLog;
